import React from "react";
import { PostOverview } from "modules/posts/models";
import ContentView from "modules/common/components/authoring/contentView/contentView";
import { ContentType } from "modules/common/models";
import PostTypeBanner from "modules/common/components/banners/postTypeBanner";
import { getTranslatedComplianceText, getTranslatedTitle } from "utils/getTranslatedContent";
import Compliance from "./compliance/compliance";
import Reactions from "./reactions/reactions";
import { ConnectedProps, connect } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import BreakingFeaturedBanner from "../banners/breakingFeaturedBanner";
import moment from "moment";
import Topics from "../post-feed/components/topics/topics";

import "./postPreviewContent.sass";

interface IPostPreviewContentProps {
    post: PostOverview;
    showComments?: boolean; // if post has comments, do we display them?
    showReactions?: boolean; // if post has reaction, do we display them?
}

const PostPreviewContent: React.FunctionComponent<PropsWithRedux> = ({
    post,
    showComments,
    showReactions,
    tenantSettings
}) => {
    const [activeLcid, setActiveLcid] = React.useState<string>(post.preferredLCID);
    const title = getTranslatedTitle(post.translatedContent, activeLcid);
    
    return (
        <ContentView
            content={{...post}}
            contentType={ContentType.Post}
            showComments={showComments}
            typeBannerComponent={<PostTypeBanner full {...post!} hideCompliance lcid={activeLcid} title={title} />}
            onChangeActiveLcid={(activeLcid) => setActiveLcid(activeLcid)}
            pageContentComponent={
                <Compliance
                    postId={post.id}
                    complianceText={getTranslatedComplianceText(post.translatedContent, activeLcid)}
                    postType={post.postType}
                />
            }
            bottomPageContentComponent={
                showReactions &&
                <Reactions
                    preview
                    reactions={tenantSettings.reactions.filter((t) => t.enabled).map((t) => ({ ...t, order: t.sortIndex }))}
                    reactionsEnabledOnTenant={tenantSettings.reactionsEnabled}
                    reactingEnabledOnPost={post.reactingEnabled}
                    postType={post.postType}
                    stats={{ postId: post.id, commentCount: 0, lastOpenTime: "", lastReadTime: "", complianceTime: "", reactions: [], viewCount: 0 }}
                />
            }
            featureBannerComponent={
                <BreakingFeaturedBanner activeLcid={activeLcid} isBreaking={post.isBreaking} isFeatured={post.isFeatured} />
            }
            pageContentInfoComponent={
                <>
                    <div className="post-content-view-published-time">
                        {
                            `Published ${moment(post.datePublished!).format("MMMM D, YYYY")}`
                        }
                    </div>
                    {
                        !!post.tags && !!post.tags.length &&
                        <Topics 
                            topics={post.tags} 
                            topicLabel={"Topics: "} 
                            className="post-content-view-tags" 
                            iconSize="small"
                        />
                    }
                </>
            }
            preview
        />
    );
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: IPostPreviewContentProps) => ({
        ...ownProps,
        tenantSettings: state.settings.tenantSettings,
    })
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(PostPreviewContent);